// app/components/SmoothProvider.tsx
'use client';

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollSmoother } from 'gsap/ScrollSmoother';

gsap.registerPlugin(ScrollTrigger, ScrollSmoother);

const SmoothContext = createContext<ScrollSmoother | null>(null);

export function SmoothProvider({ children }: { children: React.ReactNode }) {
  const [smoother, setSmoother] = useState<ScrollSmoother | null>(null);

  useEffect(() => {
    const instance = ScrollSmoother.create({
      wrapper: '#smooth-wrapper',
      content: '#smooth-content',
      smooth: 1,
      effects: false,
      normalizeScroll: true,
      smoothTouch: 0.1,
      ignoreMobileResize: true,
    });
    setSmoother(instance);

    return () => {
      instance.kill();
    };
  }, []);

  return (
    <SmoothContext.Provider value={smoother}>
      <div id='smooth-wrapper' style={{ overflow: 'hidden' }}>
        <div id='smooth-content'>{children}</div>
      </div>
    </SmoothContext.Provider>
  );
}

export function useSmoother(): ScrollSmoother {
  const ctx = useContext(SmoothContext);
  if (!ctx) {
    throw new Error('useSmoother must be used within SmoothProvider');
  }
  return ctx;
}

/**
 * Custom hook for smooth scrolling with proper error handling and fallback
 * @param delay - Optional delay before scrolling (default: 100ms)
 * @returns smoothScrollTo function
 */
export function useSmoothScrollTo(delay: number = 100) {
  const smoother = useSmoother();

  const smoothScrollTo = useCallback(
    (
      target: string,
      onComplete?: () => void,
      options?: {
        onlyIfOutside?: boolean;
        ease?: string;
        fallbackBlock?: ScrollLogicalPosition;
      }
    ) => {
      const {
        onlyIfOutside = true,
        ease = 'power2.out',
        fallbackBlock = 'start',
      } = options || {};

      // Fallback function for smooth scrolling
      const fallbackScroll = () => {
        const targetElement = document.querySelector(target);
        if (targetElement) {
          targetElement.scrollIntoView({
            behavior: 'smooth',
            block: fallbackBlock,
          });
          // Call onComplete after a reasonable delay for fallback scroll
          if (onComplete) {
            setTimeout(onComplete, 800);
          }
        }
      };

      // Add delay to ensure DOM updates
      setTimeout(() => {
        if (smoother) {
          try {
            // Use proper ScrollSmoother parameters for smooth scrolling
            smoother.scrollTo(target, onlyIfOutside, ease);
            if (onComplete) {
              onComplete();
            }
          } catch (error) {
            console.error('ScrollSmoother error:', error);
            // Fallback to native smooth scroll
            fallbackScroll();
          }
        } else {
          fallbackScroll();
        }
      }, delay);
    },
    [smoother, delay]
  );

  return smoothScrollTo;
}
